import { useEffect, useState } from "react";

// The @intility/logos registry carries every service logo as a data URI
// (about 1.5 MB of JavaScript). Keys are only known at runtime (they come from
// the API), so a single logo cannot be imported by name. This hook loads the
// registry through a dynamic import instead, so it lands in its own chunk
// rather than the main bundle, and caches the promise for every consumer.
// A failed load clears the cache: the next consumer retries instead of
// inheriting a rejected promise for the rest of the session.
export type LogoRegistry = Record<string, string>;

let registryPromise: Promise<LogoRegistry> | null = null;

function loadRegistry(): Promise<LogoRegistry> {
  if (!registryPromise) {
    registryPromise = import("@intility/logos/registry")
      .then((mod) => mod.logos as LogoRegistry)
      .catch((err) => {
        registryPromise = null;
        throw err;
      });
  }
  return registryPromise;
}

export function useLogoRegistry(): LogoRegistry | null {
  const [logos, setLogos] = useState<LogoRegistry | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadRegistry()
      .then((registry) => {
        if (!cancelled) setLogos(registry);
      })
      .catch(() => {
        // A failed chunk load leaves the registry unset, so the placeholder
        // or spinner stays; the next mount retries (see loadRegistry).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return logos;
}
